import React from "react";

import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { InvoiceSuggestion } from "../../../models";
import {
  getSuggestions,
  selectPendingSuggestions,
  selectGetSuggestions,
} from "../invoiceSuggestionsSlice";
import { ApproveOverageModal } from "../ApproveOverageModal/ApproveOverageModal";

import "./PendingOveragesCard.scss";

interface Props {
  // Called after an approve or reject so the parent can also refetch if needed.
  onActioned: () => void;
}

export function PendingOveragesCard({ onActioned }: Props) {
  const dispatch = useAppDispatch();
  const suggestions = useAppSelector(selectPendingSuggestions);
  const fetchResult = useAppSelector(selectGetSuggestions);
  const [expanded, setExpanded] = React.useState(false);
  const [activeSuggestion, setActiveSuggestion] =
    React.useState<InvoiceSuggestion | null>(null);

  const refetch = React.useCallback(() => {
    dispatch(getSuggestions());
  }, [dispatch]);

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  const handleActioned = () => {
    onActioned();
    refetch();
    setActiveSuggestion(null);
  };

  if (fetchResult.loading && suggestions.length === 0) {
    return (
      <div className="pending-overages-card loading">
        <span>Loading overages…</span>
      </div>
    );
  }

  if (fetchResult.error && suggestions.length === 0) {
    return (
      <div className="pending-overages-card error">
        <span>Could not load pending overages.</span>
      </div>
    );
  }

  const count = suggestions.length;

  return (
    <div className="pending-overages-card">
      <ApproveOverageModal
        suggestion={activeSuggestion}
        onClose={() => setActiveSuggestion(null)}
        onActioned={handleActioned}
      />
      <div className="pending-overages-summary">
        {count === 0 ? (
          <span className="caught-up">All caught up</span>
        ) : (
          <button
            className="overage-count-btn"
            onClick={() => setExpanded((x) => !x)}
          >
            {count} pending weight overage{count !== 1 ? "s" : ""}
          </button>
        )}
      </div>
      {expanded && count > 0 && (
        <div className="pending-overages-list">
          {suggestions.map((sug) => {
            const overage = Math.floor(sug.suggestedDumped - sug.suggestedAllowed);
            const total = (overage * sug.feePerPound).toFixed(2);
            return (
              <button
                key={sug.id}
                className="overage-row"
                onClick={() => setActiveSuggestion(sug)}
              >
                <span className="overage-order-name">{sug.orderName}</span>
                <span className="overage-order-address">{sug.orderAddress}</span>
                <span className="overage-weights">
                  {sug.suggestedDumped} lb dumped / {sug.suggestedAllowed} lb allowed
                </span>
                <span className="overage-amount">${total}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
